// Live commentary ticker — polls /api/events for recently-holed shots on
// today's course and slides them in as small toast-style notifications.
// Drives the "daily competition feels alive" vibe without server-push infra.

import type { HoleLayout } from "@shared/physics";
import { apiEvents, type LiveEvent } from "../api";
import { flagEmoji } from "@shared/flag";

const POLL_INTERVAL_MS = 15_000;
// Don't surface events older than this at session start — just show the
// very fresh ones (prevents a flood on first poll).
const STARTUP_LOOKBACK_MS = 60_000;
// Show each event for this long.
const EVENT_DISPLAY_MS = 5_000;
// Stagger rendering of batched events so they don't pile on at once.
const EVENT_STAGGER_MS = 600;
// Hard cap on concurrent on-screen events.
const MAX_CONCURRENT = 3;

export type TickerInstallOptions = {
  container: HTMLElement;
  courseDate: string;
  holes: HoleLayout[];
  myHandle: string;
};

export interface TickerHandle {
  dispose(): void;
}

export function installTicker(opts: TickerInstallOptions): TickerHandle {
  const { container, courseDate, holes, myHandle } = opts;

  // Track which event IDs we've already shown so re-polls don't double-fire.
  const seen = new Set<string>();
  // Track the latest createdAt we've fetched so subsequent polls request newer.
  let since = Date.now() - STARTUP_LOOKBACK_MS;

  // A little event queue so a batch of new events doesn't stack up.
  const queue: LiveEvent[] = [];
  let visibleCount = 0;
  let disposed = false;
  let flushTimer: number | null = null;

  function scheduleFlush(): void {
    if (flushTimer !== null || disposed) return;
    flushTimer = window.setTimeout(() => {
      flushTimer = null;
      flushOne();
    }, visibleCount > 0 ? EVENT_STAGGER_MS : 0);
  }

  function flushOne(): void {
    if (disposed) return;
    if (queue.length === 0) return;
    if (visibleCount >= MAX_CONCURRENT) {
      // Try again once one fades out.
      scheduleFlush();
      return;
    }
    const ev = queue.shift()!;
    showEvent(ev);
    if (queue.length > 0) scheduleFlush();
  }

  function showEvent(ev: LiveEvent): void {
    const hole = holes[ev.holeIndex];
    const par = hole?.par ?? 3;
    const delta = ev.strokes - par;
    const outcome = ev.outcome ?? "holed";
    const isMe = ev.handle.toLowerCase() === myHandle.toLowerCase();
    const flag = flagEmoji(ev.countryCode);
    const el = document.createElement("div");
    let cls: string;
    let verb: string;
    let scoreFragment: string;

    if (outcome === "holed") {
      const classForDelta =
        delta <= -2
          ? "eagle"
          : delta === -1
            ? "birdie"
            : delta === 0
              ? "par"
              : delta === 1
                ? "bogey"
                : "over";
      const scoreTag =
        ev.strokes === 1
          ? "ACE"
          : delta <= -2
            ? "EAGLE"
            : delta === -1
              ? "BIRDIE"
              : delta === 0
                ? "PAR"
                : `+${delta}`;
      cls = classForDelta;
      verb = "holed";
      scoreFragment = `${ev.strokes}${
        ev.strokes === 1 ? " · ACE!" : ` · ${scoreTag}`
      }`;
    } else if (outcome === "comet_hit") {
      cls = "comet";
      verb = "hit by comet on";
      scoreFragment = "💀";
    } else {
      // out_of_bounds
      cls = "oob";
      verb = "flew off";
      scoreFragment = "✈️";
    }

    el.className = `ticker-item ${cls}${isMe ? " me" : ""}`;
    el.innerHTML = `
      <span class="tick-flag">${flag}</span>
      <span class="tick-handle">@${escapeHtml(ev.handle)}</span>
      <span class="tick-verb">${verb}</span>
      <span class="tick-hole">H${ev.holeIndex + 1}</span>
      <span class="tick-strokes">${scoreFragment}</span>
    `;

    container.appendChild(el);
    visibleCount++;

    // Enter animation — next frame so the CSS transition fires.
    requestAnimationFrame(() => el.classList.add("open"));

    // Schedule exit.
    window.setTimeout(() => {
      el.classList.remove("open");
      el.classList.add("closing");
      window.setTimeout(() => {
        el.remove();
        visibleCount--;
        if (queue.length > 0) scheduleFlush();
      }, 280);
    }, EVENT_DISPLAY_MS);
  }

  async function poll(): Promise<void> {
    if (disposed) return;
    try {
      const res = await apiEvents(courseDate, since, 20);
      if (!res.ok) return;
      // Oldest-first so the ticker shows in time order.
      const fresh = res.events
        .filter((e) => !seen.has(e.id))
        .sort((a, b) => a.createdAt - b.createdAt);
      for (const e of fresh) {
        seen.add(e.id);
        since = Math.max(since, e.createdAt);
        queue.push(e);
      }
      if (fresh.length > 0) scheduleFlush();
    } catch {
      // swallow — keep polling
    }
  }

  // Kick off an initial poll so events that fired moments ago show up; then
  // continue polling every POLL_INTERVAL_MS.
  void poll();
  const intervalId = window.setInterval(poll, POLL_INTERVAL_MS);

  return {
    dispose() {
      disposed = true;
      window.clearInterval(intervalId);
      if (flushTimer !== null) window.clearTimeout(flushTimer);
      container.replaceChildren();
    },
  };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
